import React, { useEffect, useMemo } from 'react';
import { useField } from 'react-final-form';
import { useDataProvider, useQueryWithStore } from 'react-admin';
import { debounce } from 'lodash';
import { formatParserResponse } from 'services/helpers/formatters';

interface Targets {
  name?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  stateProvinceCode?: string;
  zipCode?: string;
  companyName?: string;
  phone?: string;
  email?: string;
  countryCode?: string;
}

interface Props {
  field: any;
  targets: Targets;
  form: any;
}

const AddressDistributor = ({ field, targets, form }: Props) => {
  const dataProvider = useDataProvider();

  const { data: countries } = useQueryWithStore({
    type: 'GET_LIST',
    resource: 'countries',
    payload: { pagination: { perPage: 999 } },
  });

  const origField = useField(field);
  const value = origField.input.value;

  const debouncedOnChange = useMemo(
    () =>
      debounce(async (value: string) => {
        if (!value) {
          return;
        }

        const { data: parsedAddress } = await dataProvider.create('parseAddress', {
          data: {
            rawAddress: value,
          },
        });

        const formattedAddress = await formatParserResponse(parsedAddress, countries, dataProvider);

        form.batch(() => {
          targets.name &&
            form.change(targets.name, formattedAddress.name || formattedAddress.companyName);
          targets.addressLine1 && form.change(targets.addressLine1, formattedAddress.addressLine1);
          targets.addressLine2 && form.change(targets.addressLine2, formattedAddress.addressLine2);
          targets.city && form.change(targets.city, formattedAddress.city);
          targets.stateProvinceCode &&
            form.change(targets.stateProvinceCode, formattedAddress.stateProvinceCode);
          targets.zipCode && form.change(targets.zipCode, formattedAddress.zipCode);
          targets.companyName && form.change(targets.companyName, formattedAddress.companyName);
          targets.phone && form.change(targets.phone, formattedAddress.phone);
          targets.email && form.change(targets.email, formattedAddress.email);
          targets.countryCode && form.change(targets.countryCode, formattedAddress.countryCode);
        });

        targets.stateProvinceCode &&
          form.change(targets.stateProvinceCode, formattedAddress.stateProvinceCode);
      }, 2000),
    [countries, dataProvider] //eslint-disable-line
  );

  useEffect(() => {
    debouncedOnChange(value);
  }, [value, debouncedOnChange]);

  return <></>;
};

export default AddressDistributor;
